import React, { useState, useEffect, useRef } from 'react';
import { useStripeCheckoutStatus } from '@/hooks/useStripeCheckoutStatus';
import {
  MapPin,
  Calendar,
  Play,
  Pause,
  Volume2,
  VolumeX,
  ChevronLeft,
  ChevronRight,
  X,
  Lock,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/Tabs";
import { useAuth } from '@/lib/auth/store';
import { AuthModal } from "@/components/auth/AuthModal";
import { MetaTags } from "@/components/MetaTags";
import { ItineraryCarousel } from "@/components/experiences/ItineraryCarousel";
import { GoogleMap } from "@react-google-maps/api";
import { createBookingAndRedirectToStripe } from '@/lib/stripe';
import { toast } from "@/lib/utils/toast";

// Add type declaration for window.google and initMap
declare global {
  interface Window {
    google: any;
    initMap?: () => void;
  }
}

function useGoogleMapsApi() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // If Google Maps is already loaded, set isLoaded to true
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      return;
    }

    // If Google Maps script is loading, set up a callback
    const originalInitMap = window.initMap;
    window.initMap = () => {
      // Call the original callback if it exists
      if (originalInitMap) {
        originalInitMap();
      }
      console.log('Google Maps API loaded via callback');
      setIsLoaded(true);
    };

    // Add a safety mechanism to check if the Google Maps API is loaded
    const checkGoogleMapsLoaded = setInterval(() => {
      if (window.google && window.google.maps) {
        console.log('Google Maps API loaded via interval check');
        setIsLoaded(true);
        clearInterval(checkGoogleMapsLoaded);
      }
    }, 500);

    return () => clearInterval(checkGoogleMapsLoaded);
  }, []);

  return isLoaded;
}

interface LocationProps {
  location: {
    name: string;
    address: string;
    city: string;
    country: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
}

function MapComponent({ location }: LocationProps) {
  const isGoogleMapsLoaded = useGoogleMapsApi();
  const [map, setMap] = useState<any>(null);

  // Create marker when map is available
  useEffect(() => {
    if (map && window.google && window.google.maps) {
      try {
        console.log("Creating marker for location:", location);
        const position = {
          lat: location.coordinates.latitude,
          lng: location.coordinates.longitude,
        };

        // Create a standard marker
        const marker = new window.google.maps.Marker({
          map,
          position,
          title: `${location.name} - ${location.address}`,
          animation: window.google.maps.Animation.DROP,
        });

        // Create an info window
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; max-width: 200px;">
              <h3 style="margin: 0 0 8px; font-weight: bold;">${location.name}</h3>
              <p style="margin: 0 0 4px;">${location.address}</p>
              <p style="margin: 0;">${location.city}, ${location.country}</p>
            </div>
          `,
        });

        // Add click listener to show info window
        marker.addListener("click", () => {
          infoWindow.open(map, marker);
        });

        // Add a simple bounce animation
        setTimeout(() => {
          if (marker) {
            // Use the built-in BOUNCE animation
            marker.setAnimation(window.google.maps.Animation.BOUNCE);

            // Stop the animation after 2 seconds
            setTimeout(() => {
              marker.setAnimation(null);
            }, 2000);
          }
        }, 500);
      } catch (error) {
        console.error("Error creating marker:", error);
      }
    }
  }, [map, location]);

  if (!isGoogleMapsLoaded) {
    return (
      <div className="w-full h-full bg-earth-800 flex items-center justify-center">
        <p className="text-sand-300">Loading map...</p>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={{
        width: "100%",
        height: "100%",
      }}
      center={{
        lat: location.coordinates.latitude,
        lng: location.coordinates.longitude,
      }}
      zoom={15}
      options={{
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: true,
        mapTypeId: "hybrid",
        mapTypeControlOptions: {
          style: window.google?.maps?.MapTypeControlStyle?.HORIZONTAL_BAR,
          position: window.google?.maps?.ControlPosition?.TOP_RIGHT,
          mapTypeIds: ["satellite", "hybrid", "roadmap", "terrain"],
        },
        scaleControl: true,
        streetViewControl: true,
        rotateControl: true,
        fullscreenControl: true,
      }}
      onLoad={(mapInstance) => {
        console.log("Map loaded successfully");
        setMap(mapInstance);
      }}
    />
  );
}

interface MediaViewerProps {
  media: Array<{ type: "image" | "video"; url: string; thumbnail?: string }>;
  className?: string;
}

function MediaViewer({ media, className = "" }: MediaViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((current) =>
      current === 0 ? media.length - 1 : current - 1,
    );
  };

  const goToNext = () => {
    setCurrentIndex((current) =>
      current === media.length - 1 ? 0 : current + 1,
    );
  };

  const currentMedia = media[currentIndex];

  return (
    <div className={`relative ${className}`}>
      <div className="aspect-video rounded-lg overflow-hidden">
        {currentMedia.type === "video" ? (
          <video
            src={currentMedia.url}
            poster={currentMedia.thumbnail}
            controls
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src={currentMedia.url}
            alt="Location highlight"
            className="w-full h-full object-cover"
          />
        )}
      </div>

      {media.length > 1 && (
        <>
          <Button
            variant="secondary"
            size="sm"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-earth-900/80 hover:bg-earth-900 backdrop-blur-sm w-8 h-8 p-0"
            onClick={goToPrevious}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <Button
            variant="secondary"
            size="sm"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-earth-900/80 hover:bg-earth-900 backdrop-blur-sm w-8 h-8 p-0"
            onClick={goToNext}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );
}

interface LodgingOption {
  name: string;
  description: string;
  price: number;
  capacity: number;
  bedrooms: number;
  amenities: string[];
  imageUrl: string;
  priceId: string;
}

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  lodgingOptions: LodgingOption[];
}

function CheckoutModal({
  isOpen,
  onClose,
  lodgingOptions,
}: CheckoutModalProps) {
  const { user } = useAuth();
  const [selectedOption, setSelectedOption] = useState<LodgingOption | null>(
    null,
  );
  const [quantity, setQuantity] = useState(1);
  const [showIndividualTickets, setShowIndividualTickets] = useState(false);
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);

  if (!isOpen) return null;

  const handleProceedToCheckout = async () => {
    console.log('🔍 Starting checkout process...');
    console.log('🔍 User state:', user ? `Authenticated as ${user.email} (${user.id})` : 'Not authenticated');
    console.log('🔍 Selection state:', { selectedOption, showIndividualTickets, quantity });
    
    if (!selectedOption && !showIndividualTickets) {
      console.log('❌ No option selected');
      return;
    }
    
    if (!user) {
      console.log('❌ User not authenticated');
      toast.error("Please sign in to continue with your booking");
      return;
    }

    setIsCreatingBooking(true);
    console.log('🔄 Setting isCreatingBooking to true');
    
    try {
      // Get the Azores experience ID
      const experienceId = "00f22911-c493-4808-9799-5092ef408474";
      
      // Calculate total amount based on selection
      let totalAmount = 0;
      if (showIndividualTickets) {
        totalAmount = 1500 * quantity;
        console.log('🔍 Individual ticket selected:', { quantity, totalAmount });
      } else if (selectedOption) {
        const option = lodgingOptions.find((o) => o.name === selectedOption.name);
        if (option) {
          totalAmount = option.price;
          console.log('🔍 Lodging option selected:', { name: option.name, price: option.price });
        }
      }
      
      // Get the price ID from the selected lodging option
      const priceId = showIndividualTickets 
        ? "price_1R8pgaI8q53ewpK1hPBZoqJL" // Individual ticket price ID
        : selectedOption?.priceId;

      if (!priceId) {
        toast.error("No price ID available for the selected option.");
        setIsCreatingBooking(false);
        return;
      }
      
      // Use the generalized function to create booking and redirect to Stripe
      await createBookingAndRedirectToStripe(
        experienceId,
        priceId,
        showIndividualTickets ? quantity : (selectedOption?.capacity || 1),
        totalAmount,
        '/experiences/azores' // Return to this experience page after checkout
      );
      
      onClose();
    } catch (error) {
      console.error('❌ Error in checkout process:', error);
    } finally {
      setIsCreatingBooking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-earth-900/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-earth-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-earth-700">
          <h2 className="text-2xl font-display">Select Your Package</h2>
          <button
            onClick={onClose}
            className="text-sand-400 hover:text-sand-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => setShowIndividualTickets(false)}
                className={`flex-1 py-3 px-4 rounded-lg ${!showIndividualTickets ? "bg-sand-400/20 border border-sand-400" : "bg-earth-700 border border-earth-700"}`}
              >
                Entire House
              </button>
              <button
                onClick={() => setShowIndividualTickets(true)}
                className={`flex-1 py-3 px-4 rounded-lg ${showIndividualTickets ? "bg-sand-400/20 border border-sand-400" : "bg-earth-700 border border-earth-700"}`}
              >
                Individual Ticket
              </button>
            </div>

            {showIndividualTickets ? (
              <div className="space-y-6">
                <h3 className="text-xl font-display">Individual Ticket</h3>
                <div className="bg-earth-700/50 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-sand-100">
                      Individual Ticket
                    </h4>
                    <span className="text-xl font-display text-sand-100">
                      €1,500
                    </span>
                  </div>
                  <p className="text-sm text-sand-400 mb-4">
                    Price to share a room with other attendee
                  </p>

                  <div className="flex items-center gap-4 mb-4">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-8 h-8 flex items-center justify-center bg-earth-800 rounded-lg"
                    >
                      -
                    </button>
                    <span className="text-sand-300">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-8 h-8 flex items-center justify-center bg-earth-800 rounded-lg"
                    >
                      +
                    </button>
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t border-earth-600">
                    <span className="font-medium text-sand-300">Total</span>
                    <span className="text-xl font-display text-sand-100">
                      €{1500 * quantity}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <h3 className="text-xl font-display">
                  Price for Full Experience for All Guests
                </h3>
                <div className="grid gap-4">
                  <div
                    className={`bg-earth-700/50 p-4 rounded-lg cursor-pointer border ${selectedOption?.name.includes("1BR") ? "border-sand-400" : "border-transparent"}`}
                    onClick={() => setSelectedOption(lodgingOptions[0])}
                  >
                    <div className="flex gap-4">
                      <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={lodgingOptions[0].imageUrl}
                          alt="1BR"
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-sand-100">
                            1BR - €3,000
                          </h4>
                        </div>

                        <p className="text-sm text-sand-400 mb-2">
                          {lodgingOptions[0].description}
                        </p>

                        <div className="flex gap-4 text-xs text-sand-300">
                          <span>Up to 2 guests</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-4 border-t border-earth-600 mt-4">
                      <span className="text-sm text-sand-400">
                        Price for Full Experience for All Guests
                      </span>
                      <Button size="sm">Purchase Full Experience</Button>
                    </div>
                  </div>

                  <div
                    className={`bg-earth-700/50 p-4 rounded-lg cursor-pointer border ${selectedOption?.name.includes("2BR") ? "border-sand-400" : "border-transparent"}`}
                    onClick={() => setSelectedOption(lodgingOptions[1])}
                  >
                    <div className="flex gap-4">
                      <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={lodgingOptions[1].imageUrl}
                          alt="2BR"
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-sand-100">
                            2BR - €6,000
                          </h4>
                        </div>

                        <p className="text-sm text-sand-400 mb-2">
                          {lodgingOptions[1].description}
                        </p>

                        <div className="flex gap-4 text-xs text-sand-300">
                          <span>Up to 4 guests</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-4 border-t border-earth-600 mt-4">
                      <span className="text-sm text-sand-400">
                        Price for Full Experience for All Guests
                      </span>
                      <Button size="sm">Purchase Full Experience</Button>
                    </div>
                  </div>

                  <div
                    className={`bg-earth-700/50 p-4 rounded-lg cursor-pointer border ${selectedOption?.name.includes("3BR") ? "border-sand-400" : "border-transparent"}`}
                    onClick={() => setSelectedOption(lodgingOptions[2])}
                  >
                    <div className="flex gap-4">
                      <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={lodgingOptions[2].imageUrl}
                          alt="3BR"
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-sand-100">
                            3BR - €9,000
                          </h4>
                        </div>

                        <p className="text-sm text-sand-400 mb-2">
                          {lodgingOptions[2].description}
                        </p>

                        <div className="flex gap-4 text-xs text-sand-300">
                          <span>Up to 6 guests</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-4 border-t border-earth-600 mt-4">
                      <span className="text-sm text-sand-400">
                        Price for Full Experience for All Guests
                      </span>
                      <Button size="sm">Purchase Full Experience</Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-6 border-t border-earth-700">
              <Button
                className="w-full"
                onClick={handleProceedToCheckout}
                disabled={(!showIndividualTickets && !selectedOption) || isCreatingBooking}
              >
                {isCreatingBooking ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Booking...
                  </>
                ) : (
                  "Proceed to Checkout"
                )}
              </Button>
              <p className="text-sand-400 text-xs mt-2 text-center">
                You will be redirected to our secure payment processor
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AzoresExperience() {
  // Handle Stripe checkout status when returning from payment
  useStripeCheckoutStatus({
    customSuccessMessage: "Your Azores experience booking is confirmed! Please check your email for details.",
    customCancelMessage: "Your Azores booking was canceled. No payment was processed."
  });
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);

  // Media gallery
  const media = [
    {
      type: "video" as const,
      url: "https://rendezvouswebsite.b-cdn.net/Azores/Sequence%2001.mp4",
      thumbnail:
        "https://rendezvouswebsite.b-cdn.net/Azores/Azores%20Still.png",
    },
  ];

  // Tab-specific media
  const tabMedia = {
    itinerary: [
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Magma%20Restaurant.jpg",
      },
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Whale%20Watching.jpg",
      },
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Pico%20Volcano.jpg",
      },
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Vineyards.jpg",
      },
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Cliffside.jpg",
      },
    ],
    location: [
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Azores%20Landscape.png",
      },
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Natural%20Swimming%20Pools.jpg",
      },
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Pico%20Vineyards.png",
      },
    ],
    accommodation: [
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Lava%20Homes%20Overhead.jpg",
      },
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Lava%20Homes%20Interior.jpg",
      },
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Lava%20Homes%20Infinity%20Pool.jpg",
      },
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Lave%20Homes%20view.jpg",
      },
    ],
    lodging: [
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Multiple%20Houses.jpg",
      },
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/House%20Layout.jpg",
      },
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/king%20bed.jpg",
      },
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Yoga%20Shala.jpg",
      },
      {
        type: "image" as const,
        url: "https://rendezvouswebsite.b-cdn.net/Azores/Hammocks.jpg",
      },
    ],
  };

  // Lodging options
  const lodgingOptions: LodgingOption[] = [
    {
      name: "1BR House",
      description: "A bedroom, a bathroom and living room with kitchenette",
      price: 3000,
      capacity: 2,
      bedrooms: 1,
      amenities: ["Kitchenette", "Bathroom", "Living room"],
      imageUrl: "https://rendezvouswebsite.b-cdn.net/Azores/1BR.jpg",
      priceId: "price_1R8piCI8q53ewpK10t30kUSd",
    },
    {
      name: "2BR House",
      description:
        "Two bedrooms (one with double bed, the other with two beds that can be joined), a bathroom and living room with kitchenette",
      price: 6000,
      capacity: 4,
      bedrooms: 2,
      amenities: ["Kitchenette", "Bathroom", "Living room"],
      imageUrl: "https://rendezvouswebsite.b-cdn.net/Azores/2BR.jpg",
      priceId: "price_1R8pjBI8q53ewpK1qxwOmgyR",
    },
    {
      name: "3BR House",
      description:
        "Three bedrooms (one with double bed, one with two beds that can be joined and a suite also with two beds that can be joined), a separate bathroom for service of the first two bedrooms and living room with kitchenette",
      price: 9000,
      capacity: 6,
      bedrooms: 3,
      amenities: ["Kitchenette", "Two bathrooms", "Living room"],
      imageUrl: "https://rendezvouswebsite.b-cdn.net/Azores/3BR.jpg",
      priceId: "price_1R8pjPI8q53ewpK1oJlyIyED",
    },
  ];

  const currentMedia = media[currentMediaIndex];

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video
        .play()
        .then(() => setIsVideoPlaying(true))
        .catch((error) => {
          console.error("Play failed:", error);
          setIsVideoPlaying(false);
        });
    } else {
      video.pause();
      setIsVideoPlaying(false);
    }
  };

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const location = {
    name: "Lava Homes",
    address: "Travessa do Outeiro das Eiras 2A, 9940-185 Terra Alta",
    city: "Pico Island",
    country: "Portugal",
    coordinates: {
      latitude: 38.45302,
      longitude: -28.14645,
    },
  };

  const faqs = [
    {
      question: "What's included in the experience?",
      answer:
        "The experience includes luxury accommodation at Lava Homes, daily breakfast, guided tours, wine tastings, and transportation on the island. Flights to and from Pico Island are not included.",
    },
    {
      question: "What should I pack?",
      answer:
        "Pack comfortable walking shoes, swimwear, light layers for variable weather, and a camera to capture the stunning landscapes. A detailed packing list will be provided upon booking.",
    },
    {
      question: "What's the weather like?",
      answer:
        "May-June offers mild temperatures around 20°C (68°F) with occasional rain showers. The weather can change quickly, so be prepared for variable conditions.",
    },
    {
      question: "How physically demanding is this experience?",
      answer:
        "The experience includes moderate hiking and walking. While no special fitness level is required, comfort with walking on varied terrain for 2-3 hours is recommended.",
    },
  ];

  const itinerary = [
    {
      day: "Day 1 - May 28th",
      activities: [
        "Airport pickup and transfer to Lava Homes",
        "Welcome dinner featuring local Azorean cuisine",
      ],
    },
    {
      day: "Day 2 - May 29th",
      activities: [
        "Breakfast at hotel",
        "Morning wellness program",
        "Whale watching boat tour (group 1)",
        "Pico Volcano hike (group 2)",
        "Sunset cocktail reception",
        "Dinner in downtown Pico",
      ],
    },
    {
      day: "Day 3 - May 30th",
      activities: [
        "Breakfast at hotel",
        "Morning wellness program",
        "Whale watching boat tour (group 2)",
        "Pico Volcano hike (group 1)",
        "Philharmonic music and dinner party",
      ],
    },
    {
      day: "Day 4 - May 31st",
      activities: [
        "Breakfast at hotel",
        "Morning wellness program",
        "Wine tasting and nibbles at Azorean Vineyard",
        "Farewell dinner celebration",
      ],
    },
    {
      day: "Day 5 - June 1st",
      activities: [
        "Breakfast at hotel",
        "Morning at leisure",
        "Transfer to airport",
        "Departure assistance",
      ],
    },
  ];

  const included = [
    "Luxury Accommodation – 5 days, 4 nights at Lava Homes, a boutique villa complex with stunning ocean views.",
    "Island Transportation – All transfers and transport throughout the retreat.",
    "Gourmet Dining – Daily breakfasts and curated dinners featuring the finest local cuisine.",
    "Adventure & Exploration – A guided hike up Pico Mountain, Portugal's highest peak.",
    "Whale Watching Excursion – Witness the majestic marine life of the Azores.",
    "Wine Tasting Experience – Explore Pico's UNESCO-listed vineyards with exclusive tastings.",
    "Wellness Program – Details being finalized.",
    "Surprise Experiences – Special moments designed to elevate your journey.",
  ];

  const notIncluded = [
    "Flights to/from Pico Island",
    "Lunches",
    "Any activities, meals, or expenses not expressly mentioned in the itinerary",
  ];

  const handleBookNowClick = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    setShowCheckoutModal(true);
  };

  return (
    <div className="min-h-screen bg-earth-900">
      <MetaTags
        title="Discover Pico Island - rendezvous"
        description="A journey to the hidden gem of the Azores. Join us in Pico Island, May 28 - June 1."
        image="https://rendezvouswebsite.b-cdn.net/Azores/azores-1.jpg"
        url="https://letsrendezvous.co/experiences/azores"
      />

      {/* Hero Media */}
      <div className="relative h-[100vh]">
        {currentMedia.type === "video" ? (
          <video
            ref={videoRef}
            src={currentMedia.url}
            className="w-full h-full object-cover"
            playsInline
            loop
            muted={isMuted}
            poster={currentMedia.thumbnail}
          />
        ) : (
          <img
            src={currentMedia.url}
            alt="Azores Experience"
            className="w-full h-full object-cover"
          />
        )}

        {/* Media Controls - Moved to bottom right */}
        {currentMedia.type === "video" && (
          <div className="absolute bottom-32 right-4 z-10 flex items-center gap-4 px-6 py-3 bg-earth-900/90 backdrop-blur-sm rounded-full">
            <button
              onClick={handlePlayPause}
              className="text-sand-100 hover:text-sand-200 transition-colors"
              aria-label={isVideoPlaying ? "Pause" : "Play"}
            >
              {isVideoPlaying ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </button>
            <div className="w-px h-4 bg-earth-700" />
            <button
              onClick={handleToggleMute}
              className="text-sand-100 hover:text-sand-200 transition-colors"
              aria-label={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </button>
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-earth-900 via-earth-900/50 to-transparent" />

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0">
          <div className="container mx-auto px-4 py-8">
            <h1 className="text-4xl md:text-5xl font-display mb-6">
              Discover Pico Island
            </h1>
            <div className="flex flex-wrap gap-6 text-sand-300 mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span>May 28 - June 1, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Pico Island, Azores</span>
              </div>
            </div>

            {/* Book Now Button - Moved higher up to avoid conflict with media controls */}
            <Button
              size="lg"
              onClick={handleBookNowClick}
              className="px-8 py-4 text-lg"
            >
              PURCHASE FULL EXPERIENCE
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* About Section */}
          <div className="bg-earth-800/50 p-6 rounded-lg">
            <h2 className="text-2xl font-display mb-6">
              About this Experience
            </h2>
            <div className="prose prose-invert prose-sand max-w-none">
              <p>
                BTG Productions presents its first adventure of 2025—a five-day
                immersive retreat on the remote and breathtaking Pico Island in
                the Azores. Designed for those seeking a perfect balance of
                adventure and relaxation, this experience invites you to
                reconnect with nature, savor world-class food and wine, and
                embrace the island's serene rhythm. Guests will stay at Lava
                Homes, one of Pico's premier luxury boutique villa complexes,
                offering stunning views and exceptional comfort.
              </p>
              <p className="mt-6">What to expect:</p>
              <ul className="list-none space-y-4 mt-4">
                <li className="flex items-start gap-2">
                  <span className="text-xl">🌋</span>
                  <span>
                    Hiking & Exploration – Conquer the majestic Mount Pico,
                    Portugal's highest peak, or embark on scenic coastal trails
                    revealing dramatic cliffs, volcanic rock formations, and
                    lush vineyards.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-xl">🐋</span>
                  <span>
                    Whale & Dolphin Watching – Set off on a unforgettable marine
                    adventure, where expert guides will take you to witness the
                    magnificent whales and playful dolphins that call these
                    waters home.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-xl">🍷</span>
                  <span>
                    Wine Tasting in UNESCO Vineyards – Discover Pico's unique
                    volcanic wines with an exclusive tasting in century-old
                    vineyards, paired with artisanal cheeses.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-xl">🧘</span>
                  <span>
                    Wellness & Relaxation – Wellness programming being
                    finalized.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Tabbed Content */}
          <div className="bg-earth-800/50 rounded-lg overflow-hidden">
            <Tabs defaultValue="itinerary">
              <div className="overflow-x-auto -mx-4 px-4">
                <TabsList className="w-full min-w-max border-b border-earth-700">
                  <TabsTrigger value="itinerary">Itinerary</TabsTrigger>
                  <TabsTrigger value="location">Location</TabsTrigger>
                  <TabsTrigger value="accommodation">Accommodation</TabsTrigger>
                  <TabsTrigger value="lodging">Lodging Types</TabsTrigger>
                  <TabsTrigger value="getting-here">Getting Here</TabsTrigger>
                  <TabsTrigger value="included">What's Included</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="itinerary" className="p-8">
                <ItineraryCarousel
                  itinerary={itinerary}
                  media={tabMedia.itinerary}
                  className="mb-8"
                />
              </TabsContent>

              <TabsContent value="location" className="p-8">
                <div className="space-y-8">
                  <MediaViewer media={tabMedia.location} className="mb-8" />
                  <div>
                    <h3 className="text-xl font-display mb-4">Pico Island</h3>
                    <p className="text-sand-300 mb-6">
                      Rising majestically from the Atlantic, Pico Island is a
                      testament to nature's raw beauty and human ingenuity.
                      Named after Portugal's highest peak, Mount Pico, this
                      volcanic jewel of the Azores archipelago offers an
                      extraordinary canvas where centuries-old vineyards weave
                      through black basalt walls, creating a UNESCO World
                      Heritage landscape unlike anywhere else in the world.
                    </p>
                    <p className="text-sand-300 mb-6">
                      Here, tradition and luxury intertwine seamlessly. The
                      island's rugged coastline is dotted with natural swimming
                      pools carved from volcanic rock, while its interior
                      reveals a tapestry of ancient lava flows and mystical
                      caves. The waters surrounding Pico are a sanctuary for
                      over 20 species of whales and dolphins, making it one of
                      the world's premier locations for marine life encounters.
                    </p>
                    <p className="text-sand-300">
                      In spring, the island transforms into a canvas of vibrant
                      colors as wildflowers bloom among the vineyards, creating
                      the perfect backdrop for our carefully curated experience.
                      The moderate climate, combined with the island's unique
                      geological features, produces exceptional wines that tell
                      the story of generations of resourceful islanders who
                      transformed volcanic soil into liquid gold.
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="accommodation" className="p-8">
                <div className="space-y-8">
                  <MediaViewer
                    media={tabMedia.accommodation}
                    className="mb-8"
                  />
                  <div>
                    <h3 className="text-xl font-display mb-4">
                      Your Stay at Lava Homes
                    </h3>
                    <p className="text-sand-300 mb-6">
                      Lava Homes, our base for this experience, offers a perfect
                      blend of luxury and authenticity. Perched on the volcanic
                      coastline of Pico Island, it provides stunning views of
                      the Atlantic Ocean and neighboring islands.
                    </p>
                    <p className="text-sand-300 mb-6">
                      The property's unique architecture harmoniously integrates
                      with the volcanic landscape, offering a serene retreat
                      after each day's adventures. Its strategic location
                      provides easy access to the island's main attractions,
                      including the UNESCO World Heritage vineyards and Mount
                      Pico.
                    </p>
                    <p className="text-sand-300 mb-6">
                      Experience luxury accommodation that perfectly balances
                      modern comfort with the island's natural beauty. Each room
                      at Lava Homes offers stunning ocean views and premium
                      amenities.
                    </p>
                    <p className="text-sand-300 mb-6">Our rooms feature:</p>
                    <ul className="list-disc list-inside text-sand-300 space-y-2 mb-6">
                      <li>Private terraces with ocean views</li>
                      <li>King-size beds with premium linens</li>
                      <li>En-suite bathrooms with rainfall showers</li>
                      <li>Locally crafted furniture and artwork</li>
                      <li>Complimentary Wi-Fi</li>
                      <li>Daily housekeeping service</li>
                    </ul>
                    <p className="text-sand-300 mb-8">
                      The property also includes a heated infinity pool, spa
                      facilities, and a gourmet restaurant featuring local
                      ingredients and wines.
                    </p>

                    <div
                      className="w-full h-[500px] rounded-lg overflow-hidden"
                      id="venue-map"
                    >
                      <MapComponent location={location} />
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="lodging" className="p-8">
                <div className="space-y-8">
                  <MediaViewer media={tabMedia.lodging} className="mb-8" />
                  <div>
                    <h3 className="text-xl font-display mb-4">
                      Lodging Options
                    </h3>
                    <p className="text-sand-300 mb-6">
                      Lava Homes offers various types of accommodations to suit
                      different preferences and group sizes. Each uniquely
                      designed space combines contemporary luxury with the
                      island's volcanic character.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <div className="aspect-video rounded-lg overflow-hidden mb-4">
                          <img
                            src="https://rendezvouswebsite.b-cdn.net/Azores/1BR.jpg"
                            alt="1BR House"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h4 className="text-lg font-display text-sand-100 mb-2">
                          1BR - €3,000
                        </h4>
                        <p className="text-sand-400 text-sm mb-4">
                          A bedroom, a bathroom and living room with kitchenette
                        </p>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sand-300">1 bedroom</span>
                          <span className="text-sand-300">Up to 2 guests</span>
                        </div>
                        <div className="flex justify-between items-center pt-4 border-t border-earth-700">
                          <span className="text-sm text-sand-400">
                            Price for Full Experience for All Guests
                          </span>
                        </div>
                      </div>

                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <div className="aspect-video rounded-lg overflow-hidden mb-4">
                          <img
                            src="https://rendezvouswebsite.b-cdn.net/Azores/2BR.jpg"
                            alt="2BR House"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h4 className="text-lg font-display text-sand-100 mb-2">
                          2BR - €6,000
                        </h4>
                        <p className="text-sand-400 text-sm mb-4">
                          Two bedrooms (one with double bed, the other with two
                          beds that can be joined), a bathroom and living room
                          with kitchenette
                        </p>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sand-300">2 bedrooms</span>
                          <span className="text-sand-300">Up to 4 guests</span>
                        </div>
                        <div className="flex justify-between items-center pt-4 border-t border-earth-700">
                          <span className="text-sm text-sand-400">
                            Price for Full Experience for All Guests
                          </span>
                        </div>
                      </div>

                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <div className="aspect-video rounded-lg overflow-hidden mb-4">
                          <img
                            src="https://rendezvouswebsite.b-cdn.net/Azores/3BR.jpg"
                            alt="3BR House"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h4 className="text-lg font-display text-sand-100 mb-2">
                          3BR - €9,000
                        </h4>
                        <p className="text-sand-400 text-sm mb-4">
                          Three bedrooms (one with double bed, one with two beds
                          that can be joined and a suite also with two beds that
                          can be joined), a separate bathroom for service of the
                          first two bedrooms and living room with kitchenette
                        </p>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sand-300">3 bedrooms</span>
                          <span className="text-sand-300">Up to 6 guests</span>
                        </div>
                        <div className="flex justify-between items-center pt-4 border-t border-earth-700">
                          <span className="text-sm text-sand-400">
                            Price for Full Experience for All Guests
                          </span>
                        </div>
                      </div>

                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <div className="aspect-video rounded-lg overflow-hidden mb-4">
                          <img
                            src="https://rendezvouswebsite.b-cdn.net/Azores/Single%20Beds.jpg"
                            alt="Individual Ticket"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h4 className="text-lg font-display text-sand-100 mb-2">
                          Individual Ticket - €1,500
                        </h4>
                        <p className="text-sand-400 text-sm mb-4">
                          Price to share a room with other attendee
                        </p>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sand-300">
                            Shared accommodation
                          </span>
                          <span className="text-sand-300">1 person</span>
                        </div>
                        <div className="flex justify-between items-center pt-4 border-t border-earth-700">
                          <span className="text-sm text-sand-400">
                            Price for Full Experience
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 p-4 bg-earth-800 rounded-lg">
                      <p className="text-sand-300 mb-2">
                        All lodging options include:
                      </p>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sand-400 text-sm">
                        <li className="flex items-center gap-2">
                          <div className="w-1 h-1 rounded-full bg-sand-400"></div>
                          <span>Daily breakfast</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1 h-1 rounded-full bg-sand-400"></div>
                          <span>Access to infinity pool</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1 h-1 rounded-full bg-sand-400"></div>
                          <span>Wi-Fi throughout the property</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1 h-1 rounded-full bg-sand-400"></div>
                          <span>Daily housekeeping</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1 h-1 rounded-full bg-sand-400"></div>
                          <span>Welcome amenities</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1 h-1 rounded-full bg-sand-400"></div>
                          <a
                            href="https://lavahomes.com/en/1430404/about"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sand-400 hover:text-sand-200"
                          >
                            More details{" "}
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        </li>
                      </ul>
                    </div>

                    <div className="mt-8 flex justify-center">
                      <Button
                        onClick={handleBookNowClick}
                        size="lg"
                        className="px-8"
                      >
                        Purchase Full Experience
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="getting-here" className="p-8">
                <div className="space-y-8">
                  <div className="bg-earth-800 p-6 rounded-lg">
                    <h3 className="text-xl font-display mb-6">
                      Important Travel Information
                    </h3>
                    <div className="text-sand-300">
                      <p className="mb-4">
                        To ensure a smooth start to your Azores experience, we
                        strongly recommend arriving in mainland Portugal or São
                        Miguel by{" "}
                        <span className="text-sand-100">Tuesday, May 27th</span>
                        . This allows you to catch one of two morning flights to
                        Pico Island on Wednesday:
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                        <div className="bg-earth-700/50 p-4 rounded-lg">
                          <h4 className="font-display text-lg mb-2">
                            From Lisbon (LIS)
                          </h4>
                          <p className="text-sm space-y-1">
                            <span className="block">Azores Airlines SP522</span>
                            <span className="block text-sand-400">
                              Departs: 07:00
                            </span>
                            <span className="block text-sand-400">
                              Arrives: 08:35
                            </span>
                          </p>
                        </div>

                        <div className="bg-earth-700/50 p-4 rounded-lg">
                          <h4 className="font-display text-lg mb-2">
                            From Ponta Delgada (PDL)
                          </h4>
                          <p className="text-sm space-y-1">
                            <span className="block">Azores Airlines SP461</span>
                            <span className="block text-sand-400">
                              Departs: 08:45
                            </span>
                            <span className="block text-sand-400">
                              Arrives: 09:30
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="mt-8 p-4 bg-sand-400/10 rounded-lg">
                        <p className="text-sm">
                          <span className="text-sand-100">
                            Complimentary Transfer:
                          </span>{" "}
                          We provide transportation from Pico Airport (PIX) for
                          guests arriving on either of these flights. Our team
                          will be waiting to welcome you and ensure a seamless
                          journey to Lava Homes.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-earth-800 p-6 rounded-lg">
                    <h3 className="text-xl font-display mb-4">Travel Tips</h3>
                    <ul className="space-y-4 text-sand-300">
                      <li className="flex items-start gap-2">
                        <span className="text-xl">✈️</span>
                        <span>
                          Book your flights early, as seats to Pico can be
                          limited during peak season.
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-xl">🏨</span>
                        <span>
                          Consider spending a night in Lisbon or São Miguel if
                          you're traveling from outside Europe.
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-xl">📝</span>
                        <span>
                          Share your flight details with us after booking so we
                          can coordinate your airport transfer.
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-xl">🗺️</span>
                        <span>
                          Our team can assist with additional travel
                          arrangements or recommendations if needed.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="included" className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-1 gap-8">
                  <div>
                    <h3 className="text-xl font-display mb-4">
                      What's Included
                    </h3>
                    <ul className="list-disc list-inside text-sand-300 space-y-2">
                      {included.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-display mb-4">Not Included</h3>
                    <ul className="list-disc list-inside text-sand-300 space-y-2">
                      {notIncluded.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Add spacing before footer */}
      <div className="h-24"></div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab="sign-up"
        redirectTo={`/experiences/azores`}
      />

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        lodgingOptions={lodgingOptions}
      />
    </div>
  );
}
